import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { auditApi } from "../api";

const PAGE_SIZE = 50;

export default function AuditPage() {
  const [page, setPage] = useState(0);
  const offset = page * PAGE_SIZE;
  const { data, isLoading } = useQuery({
    queryKey: ["audit", PAGE_SIZE, offset],
    queryFn: () => auditApi.list(PAGE_SIZE, offset),
  });

  return (
    <div className="max-w-6xl mx-auto px-10 py-10">
      <header className="mb-7">
        <h1 className="display text-[36px] font-medium tracking-tight">
          Audit <span className="italic">log</span>
        </h1>
        <p className="text-[13.5px] text-ink-500 mt-1.5">A record of admin actions.</p>
      </header>

      <div className="surface overflow-hidden">
        <table className="w-full text-[12.5px]">
          <thead className="bg-cream-50 eyebrow !tracking-[0.1em]">
            <tr>
              <th className="text-left px-3.5 py-2.5 font-medium w-44">Time</th>
              <th className="text-left px-3.5 py-2.5 font-medium w-28">Admin</th>
              <th className="text-left px-3.5 py-2.5 font-medium w-48">Action</th>
              <th className="text-left px-3.5 py-2.5 font-medium">Target</th>
              <th className="text-left px-3.5 py-2.5 font-medium">Details</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-ink-100">
            {isLoading && (
              <tr>
                <td colSpan={5} className="px-3.5 py-6 text-center text-ink-500">Loading…</td>
              </tr>
            )}
            {data?.entries.map((e) => (
              <tr key={e.id} className="hover:bg-cream-50 align-top">
                <td className="px-3.5 py-2.5 text-ink-700 whitespace-nowrap font-mono text-[11.5px]">
                  {new Date(e.ts).toLocaleString()}
                </td>
                <td className="px-3.5 py-2.5 font-mono text-[11.5px]">{e.admin}</td>
                <td className="px-3.5 py-2.5">
                  <span className="chip-mono">{e.action}</span>
                </td>
                <td className="px-3.5 py-2.5 font-mono text-[11.5px]">{e.target}</td>
                <td className="px-3.5 py-2.5 text-ink-500">
                  {e.details ? (
                    <pre className="text-[11px] whitespace-pre-wrap break-all max-w-md font-mono">
                      {typeof e.details === "string"
                        ? e.details
                        : JSON.stringify(e.details, null, 0)}
                    </pre>
                  ) : (
                    "—"
                  )}
                </td>
              </tr>
            ))}
            {data?.entries.length === 0 && (
              <tr>
                <td colSpan={5} className="px-3.5 py-6 text-center text-ink-500">No entries.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between mt-4 text-[12px] text-ink-500">
        <div>
          Page <span className="display italic">{page + 1}</span> · {data?.entries.length ?? 0} shown
        </div>
        <div className="flex gap-1.5">
          <button
            disabled={page === 0}
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            className="btn-secondary btn-sm disabled:opacity-40"
          >
            <ChevronLeft className="size-3.5" /> Prev
          </button>
          <button
            disabled={!data || data.entries.length < PAGE_SIZE}
            onClick={() => setPage((p) => p + 1)}
            className="btn-secondary btn-sm disabled:opacity-40"
          >
            Next <ChevronRight className="size-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
}
